import { FlexColumn, FlexRow, FullContainer, ImageThumbnail, isValidImage } from '@gorila-shared-ui/components';
import { LabelSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useStyles } from '../../../../hooks/useStyles';
import { getS3FileUrlByKey } from '../../../../services/appService';
import { FleetDriverPhotoDetail } from '../../../../types/fleet';
import { truncateNumber } from '../../../../utils/numbers';
import ImagesCarouselFullScreenPreviewModal from '../../../shared/ImagesCarouselFullScreenPreviewModal';

type Props = Readonly<{
  initial: string;
  photos: FleetDriverPhotoDetail[];
  onClose: () => void;
  isOpen: boolean;
  header?: React.ReactNode;
  disableDownload?: boolean;
}>;

type FleetPhotoMediaProps = Readonly<{
  photo?: FleetDriverPhotoDetail;
  imageUrl?: string;
}>;

function FleetPhotoMedia({ photo, imageUrl }: FleetPhotoMediaProps) {
  const { css, theme } = useStyles();
  const [matchImageUrl, setMatchImageUrl] = useState<string>();

  useEffect(() => {
    const loadMatchImage = async () => {
      setMatchImageUrl(undefined);
      if (!photo?.matchPhoto) {
        return;
      }
      const { url, error } = await getS3FileUrlByKey(photo.matchPhoto);
      if (!error && url) {
        setMatchImageUrl(url);
      }
    };

    loadMatchImage();
  }, [photo]);

  return (
    <FullContainer
      flexible
      classNames={css({
        justifyContent: 'center',
        position: 'relative',
      })}
    >
      {imageUrl && isValidImage(imageUrl) && (
        <img
          src={imageUrl}
          alt=""
          className={css({
            height: '100%',
            maxWidth: '100%',
            objectFit: 'contain',
          })}
        />
      )}

      {matchImageUrl && isValidImage(matchImageUrl) && photo?.similarity != null && photo?.confidence != null && (
        <FlexRow
          classNames={css({
            position: 'absolute',
            right: '50%',
            bottom: 0,
            background: theme.colors.backgroundInversePrimary,
            borderRadius: theme.borders.buttonBorderRadius,
            overflow: 'hidden',
            transform: 'translateX(50%) translateY(10px)',

            [theme.mediaQuery.medium]: {
              transform: 'translateX(120%) translateY(10px)',
            },
          })}
          gap={0}
        >
          <ImageThumbnail
            url={matchImageUrl}
            imageStyle={{
              height: '140px',
              width: 'auto',
              objectFit: 'contain',
              objectPosition: 'left',
              minWidth: '87px',
            }}
          />

          <FlexColumn
            gap={theme.sizing.scale200}
            padding={theme.sizing.scale600}
            classNames={css({
              justifyContent: 'center',
            })}
          >
            <LabelSmall color={theme.colors.contentInversePrimary}>Coincidencia</LabelSmall>

            <LabelSmall color={theme.colors.contentInversePrimary}>
              Similitud: {truncateNumber(photo.similarity, 2)}%
            </LabelSmall>

            <LabelSmall color={theme.colors.contentInversePrimary}>
              Confianza: {truncateNumber(photo.confidence, 2)}%
            </LabelSmall>
          </FlexColumn>
        </FlexRow>
      )}
    </FullContainer>
  );
}

function FleetImagesCarouselFullScreenPreviewModal({
  initial,
  isOpen,
  onClose,
  photos,
  disableDownload,
  header,
}: Props) {
  const s3Keys = photos.map(({ photo }) => photo);
  const photoMap = new Map(photos.map((photo) => [photo.photo, photo]));

  return (
    <ImagesCarouselFullScreenPreviewModal
      initial={initial}
      isOpen={isOpen}
      onClose={onClose}
      s3Keys={s3Keys}
      disableDownload={disableDownload}
      header={header}
      renderMedia={({ imageUrl, cursor }) => (
        <FleetPhotoMedia
          imageUrl={imageUrl}
          photo={photoMap.get(cursor)}
        />
      )}
    />
  );
}

export default FleetImagesCarouselFullScreenPreviewModal;
